import { thumbUrl } from "../api";
import { formatJpy, formatJpyWithUsd } from "../money";
import type { BatchSummary } from "../types";
import { IconClose, IconCopy, IconTrash } from "./icons";
import { cx } from "./ui";

function when(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? `今日 ${d.toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" })}`
    : d.toLocaleString("ja-JP", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function HistoryPanel({
  open,
  batches,
  activeId,
  onOpen,
  onRestore,
  onDelete,
  onClose,
}: {
  open: boolean;
  batches: BatchSummary[];
  activeId?: string;
  onOpen: (b: BatchSummary) => void;
  onRestore: (b: BatchSummary) => void;
  onDelete: (b: BatchSummary) => void;
  onClose: () => void;
}) {
  return (
    <>
      <div
        className={cx(
          "fixed inset-0 z-40 bg-slate-900/25 transition-opacity",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={onClose}
      />
      <aside
        className={cx(
          "fixed right-0 top-0 z-40 flex h-full w-[400px] max-w-[92vw] flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-200",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h2 className="text-[15px] font-bold text-slate-800">生成履歴</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <IconClose />
          </button>
        </header>

        <div className="thin-scroll flex-1 overflow-y-auto p-3">
          {batches.length === 0 && (
            <p className="px-2 py-8 text-center text-[13px] text-slate-400">まだ履歴がありません</p>
          )}
          <ul className="space-y-2">
            {batches.map((b) => (
              <li
                key={b.id}
                className={cx(
                  "group rounded-xl border p-2.5 transition",
                  b.id === activeId ? "border-brand-400 bg-brand-50/60" : "border-slate-200 hover:border-brand-300",
                )}
              >
                <button type="button" onClick={() => onOpen(b)} className="w-full text-left">
                  <div className="mb-1.5 flex gap-1">
                    {(b.thumbs ?? []).slice(0, 4).map((t) => (
                      <img key={t} src={thumbUrl(t, 96)} alt="" className="h-11 w-11 rounded-md object-cover" />
                    ))}
                    {(b.thumbs ?? []).length === 0 && (
                      <div className="h-11 w-11 rounded-md bg-slate-100" />
                    )}
                  </div>
                  <p className="line-clamp-2 text-[13px] font-semibold leading-relaxed text-slate-700">
                    {b.title || b.prompt}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-slate-400">
                    <span>{when(b.created_at)}</span>
                    <span>·</span>
                    <span>
                      {b.done_count}/{b.total}枚
                    </span>
                    {Number(b.error_count) > 0 && <span className="text-rose-500">失敗{b.error_count}</span>}
                    <span>·</span>
                    <span title={formatJpyWithUsd(Number(b.cost_usd))}>{formatJpy(Number(b.cost_usd))}</span>
                    {b.status === "running" && (
                      <span className="rounded bg-brand-100 px-1 font-bold text-brand-700">生成中</span>
                    )}
                  </div>
                </button>
                <div className="mt-1.5 flex gap-1 opacity-0 transition group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={() => onRestore(b)}
                    className="chip flex-1 justify-center"
                    title="このときの設定をフォームに戻す"
                  >
                    <IconCopy className="h-3 w-3" />
                    設定を復元
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(b)}
                    className="chip text-slate-400 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600"
                    title="削除"
                  >
                    <IconTrash className="h-3 w-3" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </aside>
    </>
  );
}
